import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Grid, TextField, Button, FormControl,
  InputLabel, Select, MenuItem,
  Alert, CircularProgress, Avatar, IconButton
} from '@mui/material';
import { PhotoCamera, Save } from '@mui/icons-material';
import { useAuth } from '../../store/authStore';
import { updateTeacherProfile } from '../../api/marketplace';
import { getCurrentLanguage } from '../../utils/navigation';

const TeacherSettings = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const lang = getCurrentLanguage();

  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(user?.photoURL || '');

  const [settings, setSettings] = useState({
    displayName: user?.displayName || '',
    bio: user?.bio || '',
    description: user?.description || '',
    pricePerHour: user?.pricePerHour || 15,
    trialPrice: user?.trialPrice || 5,
    lessonDuration: user?.lessonDuration || 50,
    currency: user?.currency || 'USD',
    experience: user?.experience || '',
    education: user?.education || '',
    emailNotifications: true,
    bookingAutoConfirm: false
  });

  const handleChange = (field) => (e) => {
    setSettings(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      let photoURL = user?.photoURL;
      if (photoFile) {
        const reader = new FileReader();
        photoURL = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(photoFile);
        });
      }

      const updates = {
        displayName: settings.displayName,
        bio: settings.bio,
        description: settings.description,
        pricePerHour: Number(settings.pricePerHour),
        trialPrice: Number(settings.trialPrice),
        lessonDuration: Number(settings.lessonDuration),
        currency: settings.currency,
        experience: settings.experience,
        education: settings.education,
      };
      if (photoURL) updates.photoURL = photoURL;

      await updateTeacherProfile(user.id, updates);
      await updateUser({ ...user, ...updates });
      setSuccess('Settings saved successfully!');
      setPhotoFile(null);
    } catch (err) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>Settings</Typography>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Profile Photo */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>Profile Photo</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ position: 'relative' }}>
            <Avatar src={photoPreview} sx={{ width: 100, height: 100, fontSize: 40 }}>
              {user?.displayName?.charAt(0)}
            </Avatar>
            <IconButton
              component="label"
              sx={{
                position: 'absolute', bottom: 0, right: 0,
                bgcolor: 'primary.main', color: 'white',
                '&:hover': { bgcolor: 'primary.dark' }
              }}
              size="small"
            >
              <PhotoCamera fontSize="small" />
              <input type="file" hidden accept="image/*" onChange={handlePhotoChange} />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Upload a clear photo of yourself. Students prefer teachers with profile photos.
          </Typography>
        </Box>
      </Paper>

      {/* Basic Info */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>Basic Information</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth label="Display Name"
              value={settings.displayName} onChange={handleChange('displayName')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth label="Short Bio" multiline rows={2}
              value={settings.bio} onChange={handleChange('bio')}
              helperText={`${settings.bio.length}/200`}
              inputProps={{ maxLength: 200 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth label="Detailed Description" multiline rows={4}
              value={settings.description} onChange={handleChange('description')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth label="Teaching Experience" multiline rows={3}
              value={settings.experience} onChange={handleChange('experience')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth label="Education" multiline rows={2}
              value={settings.education} onChange={handleChange('education')}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Pricing */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>Pricing</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label="Price per Hour" type="number"
              value={settings.pricePerHour} onChange={handleChange('pricePerHour')}
              inputProps={{ min: 1, max: 500 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label="Trial Price" type="number"
              value={settings.trialPrice} onChange={handleChange('trialPrice')}
              inputProps={{ min: 0, max: 100 }}
              helperText="Set to 0 for free trial"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Lesson Duration</InputLabel>
              <Select value={settings.lessonDuration} onChange={handleChange('lessonDuration')} label="Lesson Duration">
                <MenuItem value={25}>25 minutes</MenuItem>
                <MenuItem value={50}>50 minutes</MenuItem>
                <MenuItem value={75}>75 minutes</MenuItem>
                <MenuItem value={90}>90 minutes</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select value={settings.currency} onChange={handleChange('currency')} label="Currency">
                <MenuItem value="USD">USD ($)</MenuItem>
                <MenuItem value="EUR">EUR (€)</MenuItem>
                <MenuItem value="MAD">MAD (د.م.)</MenuItem>
                <MenuItem value="GBP">GBP (£)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Edit full profile link */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>Full Profile Setup</Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          To update your languages, specialties, and certifications, go to the full registration page.
        </Typography>
        <Button variant="outlined" onClick={() => navigate(`/${lang}/teacher/register`)}>
          Edit Full Profile
        </Button>
      </Paper>

      {/* Save Button */}
      <Button
        variant="contained" size="large" onClick={handleSave}
        disabled={saving} startIcon={saving ? <CircularProgress size={20} /> : <Save />}
        sx={{ py: 1.5, px: 4 }}
      >
        {saving ? 'Saving...' : 'Save Changes'}
      </Button>
    </Box>
  );
};

export default TeacherSettings;
